import { useContext, useMemo } from "react";
import { ScopesContext, ScopeType } from "../ScopeProvider/ScopeProvider";
import {
  getIconClassName,
  resourceTypes,
} from "../../../Findings/FindingsUtils";
import { DotLoader, TextButton, ModalContext } from "@SeemplicityDev/shared";
import Chip from "../../../Components/Chip/Chip";
import CreateGroup from "../../../Findings/FindingsFilters/ScopeHierarchy/CreateGroup/CreateGroup";
import ScopeSettings from "../ScopeSettings/ScopeSettings";
import { UNSAVED_SCOPE } from "../../../Findings/FindingsFilters/FilterUtils";
import {
  AppPreferencesContext,
  userActionsEnum,
} from "../../../General/AppPreferencesProvider";
import { AppPreferencesContextType } from "../../../types/AppPreferencesProvider";
import { Link } from "react-router-dom";
import { getIconByType } from "../../../Components/Tree/TreeUtils";
import { SCOPES_ERROR_MESSAGE } from "../useScopesData";

interface GroupItem {
  label?: string;
  has_scope_descendants?: boolean;
  value: {
    id?: string;
    type?: ScopeType;
    editable?: boolean;
  };
}

interface GroupProps {
  children?: GroupItem[];
  groupId: string;
}

const Group: React.FC<GroupProps> = ({ children, groupId }) => {
  const { isAllowed } = useContext(
    AppPreferencesContext
  ) as AppPreferencesContextType;

  const { openModal } = useContext(ModalContext);
  const {
    searchTree,
    updateScopeState,
    scopesState: { scopesList, selectedScope },
  } = useContext(ScopesContext);

  const onEditGroup = () => {
    openModal({
      title: "Edit Group",
      className: "edit-group-modal",
      size: "s",
      overlay: true,
      component: (
        <CreateGroup
          selectedScope={selectedScope}
          groupId={groupId}
          groupName={selectedScope?.label || ""}
          root={!!scopesList ? scopesList : null}
          updateScopeState={updateScopeState}
          searchTree={searchTree}
        />
      ),
    });
  };
  return (
    <>
      {children?.map((item: GroupItem) => (
        <Chip
          key={`chip-${item.label}`}
          leftIcon={getIconClassName(item.value?.type || "")}
          text={item.label || ""}
          disabled
          rightIcon=""
          small={false}
          square={false}
          noMargin={false}
          noHover={false}
          empty={
            item.value.type === resourceTypes.GROUP &&
            !item.has_scope_descendants
          }
          tooltipContent={
            item.value.type === resourceTypes.GROUP &&
            !item.has_scope_descendants
              ? "This scope group is empty. Click Edit Group to update or delete it."
              : item.label
          }
        />
      ))}
      {isAllowed(userActionsEnum.CREATE_SCOPE) && (
        <TextButton
          data-testid={"edit-group"}
          wrapperClassName={"panel-text-btn"}
          label={"Edit Group"}
          leftIcon={getIconByType(resourceTypes.GROUP)}
          onClick={onEditGroup}
          buttonStyle={"textLink"}
        />
      )}
    </>
  );
};

interface ChipWithCreateScopeProps {
  text: string;
  icon: string;
}

const ChipWithCreateScope: React.FC<ChipWithCreateScopeProps> = ({
  text,
  icon,
}) => {
  const { isAllowed } = useContext(
    AppPreferencesContext
  ) as AppPreferencesContextType;
  const {
    updateScopeState,
    scopesState: { scopesList },
  } = useContext(ScopesContext);

  const noScopes = useMemo(() => {
    return !!scopesList && !scopesList?.[0]?.children?.length;
  }, [scopesList]);

  const setNewScope = () => {
    updateScopeState({
      isNewScope: true,
      selectedScope: {
        label: UNSAVED_SCOPE,
        level: 1, // from here always under root scope,
        value: {
          type: "scope",
        },
      },
      scopeToExecute: null,
    });
  };

  return (
    <>
      <Chip
        leftIcon={icon}
        text={text}
        disabled
        rightIcon=""
        small={false}
        square={false}
        noMargin={false}
        noHover={false}
        empty={false}
        tooltipContent=""
      />
      {(isAllowed(userActionsEnum.CREATE_SCOPE) ||
        isAllowed(userActionsEnum.SOFT_EDIT_SCOPE)) && (
        <TextButton
          data-testid={"new-scope"}
          wrapperClassName={"panel-text-btn"}
          label={"New Scope"}
          leftIcon={getIconByType(resourceTypes.SCOPE)}
          onClick={setNewScope}
          buttonStyle={"textLink"}
        />
      )}
      {noScopes && isAllowed(userActionsEnum.CREATE_SCOPE) && (
        <div className={"create-scope-hierarchy-encouragement"}>
          <i className="seem-icon-hierarchy" />
          <div className="encouragement-text">
            Try out our new Scope Hierarchy Creator!{" "}
          </div>
          <Link to={"/scopes-generator"}>
            <TextButton
              label={"Create scopes"}
              className={"go-to-scopes-generator"}
              rightIcon={"seem-icon-double-chevron-right"}
              buttonStyle={"textLink"}
              data-testid={"go-to-scopes-generator-from-scope-management"}
            />
          </Link>
        </div>
      )}
    </>
  );
};

interface SelectedScopeManagementProps {
  isLoadingScopesData: boolean;
  hasScopesError?: boolean;
  // Set to true in contexts that do not have an Apply button (e.g. Rules /
  // Automation Wizard / Remediation Queue scope inputs), where Save/Save As
  // must remain enabled even when pending unapplied changes exist.
  allowSaveWithPendingChanges?: boolean;
}

const SelectedScopeManagement = ({
  isLoadingScopesData,
  hasScopesError,
  allowSaveWithPendingChanges,
}: SelectedScopeManagementProps) => {
  const {
    scopesState: { selectedScope },
    getScopeValueById,
  } = useContext(ScopesContext);

  const isGroup = useMemo(
    () => selectedScope?.value?.type === resourceTypes.GROUP,
    [selectedScope]
  );
  const isRootScope = useMemo(
    () => selectedScope?.value?.type === resourceTypes.ROOT,
    [selectedScope]
  );

  const isUnscoped = useMemo(
    () => selectedScope?.value?.type === resourceTypes.UNSCOPED,
    [selectedScope]
  );

  if (hasScopesError && !selectedScope) {
    return (
      <div
        className={"scope-management-error"}
        data-testid={"scope-management-error"}
      >
        <i className={"seem-icon seem-icon-error"} />
        {SCOPES_ERROR_MESSAGE}
      </div>
    );
  }

  if (isLoadingScopesData || !selectedScope) {
    return <DotLoader className={"scope-dot-loader"} />;
  }

  if (isRootScope) {
    return (
      <div className={"scope-wrap-group"}>
        <ChipWithCreateScope
          text={"All Resources"}
          icon={getIconByType(resourceTypes.ROOT)}
        />
      </div>
    );
  } else if (isGroup) {
    const group = getScopeValueById(selectedScope?.value?.id!);

    return (
      <div className={"scope-wrap-group"}>
        <Group
          children={group?.value?.children as unknown as GroupItem[]}
          groupId={selectedScope?.value?.id!}
        />
      </div>
    );
  } else if (isUnscoped) {
    return (
      <div className={"scope-wrap-group"}>
        <ChipWithCreateScope
          text={"Resources Without Scope"}
          icon={"seem-icon-unscoped"}
        />
      </div>
    );
  }

  return (
    <ScopeSettings allowSaveWithPendingChanges={allowSaveWithPendingChanges} />
  );
};

export default SelectedScopeManagement;
